'use client';

/* eslint-disable @next/next/no-img-element */
import { useJump } from '@/components/MagicJump';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useEffect, useRef, useState } from 'react';
import { resolveCollaborationAssetUrl } from '../runtimeData';
import type { CollaborationBannerData } from '../types';
import styles from '../CollaborationPage.module.css';
import CollaborationVideoSoundButton from './CollaborationVideoSoundButton';

export default function CollaborationBanner({
  data,
  isWeb,
  onButtonClick,
  onExposure,
}: {
  data: CollaborationBannerData;
  isWeb: boolean;
  onButtonClick?: (banner: CollaborationBannerData) => void;
  onExposure?: (banner: CollaborationBannerData) => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoFailed, setVideoFailed] = useState(false);
  const [loadedDimensions, setLoadedDimensions] = useState<{
    source: string;
    width: number;
    height: number;
  } | null>(null);
  const toJumpTarget = useJump();
  const image = resolveCollaborationAssetUrl(isWeb ? data.pcImage : data.wapImage);
  const video = resolveCollaborationAssetUrl(isWeb ? data.pcVideo : data.wapVideo);
  const showVideo = data.materialType === 'video' && Boolean(video) && !videoFailed;
  const titleImage =
    data.titleType === 'image' ? resolveCollaborationAssetUrl(data.titleImage) : '';
  const hasTitle = data.titleType === 'image' ? Boolean(titleImage) : Boolean(data.title);
  const videoDimensions = loadedDimensions?.source === video ? loadedDimensions : null;
  const mediaDimensions =
    data.materialType === 'video'
      ? (videoDimensions ?? (isWeb ? { width: 1920, height: 560 } : { width: 1440, height: 1080 }))
      : isWeb
        ? { width: 1920, height: 450 }
        : { width: 1440, height: 1080 };
  const { ref: exposureRef } = useValidExposure({
    callback: () => onExposure?.(data),
    threshold: 0.5,
    minDuration: 500,
    ts: `${showVideo ? video : image}-${isWeb ? 'pc' : 'wap'}`,
  });

  useEffect(() => {
    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (media?.matches) videoRef.current?.pause();
  }, []);

  useEffect(() => {
    const media = videoRef.current;
    // 缓存视频可能在 hydration 前已触发 loadedmetadata，补读已就绪的尺寸。
    if (
      showVideo &&
      media &&
      media.readyState >= 1 &&
      media.videoWidth > 0 &&
      media.videoHeight > 0
    ) {
      setLoadedDimensions({ source: video, width: media.videoWidth, height: media.videoHeight });
    }
  }, [showVideo, video]);

  if (!image && !showVideo) return null;

  return (
    <section
      ref={exposureRef}
      className={`${styles.banner} ${data.materialType === 'video' ? styles.videoBanner : ''}`}
      data-video-sized={Boolean(videoDimensions)}
      style={
        data.materialType === 'video' && (videoDimensions || !image)
          ? {
              aspectRatio: `${mediaDimensions.width} / ${mediaDimensions.height}`,
            }
          : undefined
      }
      aria-label={data.title || data.alt}
    >
      {data.materialType === 'video' && image && (
        <img
          className={styles.bannerCover}
          src={image}
          alt={data.alt || data.title}
          width={mediaDimensions.width}
          height={mediaDimensions.height}
        />
      )}
      {showVideo ? (
        <video
          ref={videoRef}
          className={styles.fullMedia}
          src={video}
          poster={image}
          width={mediaDimensions.width}
          height={mediaDimensions.height}
          autoPlay
          muted
          loop
          playsInline
          aria-hidden="true"
          onLoadedMetadata={(event) => {
            const { videoWidth, videoHeight } = event.currentTarget;
            if (videoWidth > 0 && videoHeight > 0) {
              setLoadedDimensions({ source: video, width: videoWidth, height: videoHeight });
            }
          }}
          onError={() => setVideoFailed(true)}
        />
      ) : data.materialType !== 'video' ? (
        <img
          className={styles.fullMedia}
          src={image}
          alt={data.alt || data.title}
          width={mediaDimensions.width}
          height={mediaDimensions.height}
        />
      ) : null}
      {showVideo && <CollaborationVideoSoundButton key={video} videoRef={videoRef} />}
      {(hasTitle || data.buttonText) && (
        <div className={styles.bannerContent}>
          {hasTitle ? (
            <h1 style={{ color: data.titleColor }}>
              {data.titleType === 'image' ? (
                <img
                  className={styles.bannerTitleImage}
                  src={titleImage}
                  alt={data.title || data.alt}
                />
              ) : (
                data.title
              )}
            </h1>
          ) : null}
          {data.buttonText && data.buttonJump && (
            <button
              type="button"
              style={{ color: data.buttonTextColor, backgroundColor: data.buttonColor }}
              onClick={() => {
                onButtonClick?.(data);
                toJumpTarget(data.buttonJump!);
              }}
            >
              {data.buttonText}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
